import { Component, OnDestroy } from '@angular/core';
import { BehaviorSubject, combineLatest, debounceTime, distinctUntilChanged, finalize, map, Observable, of, startWith, Subscription, switchMap, tap } from 'rxjs';
import { Pagination } from 'src/app/accountancy/utilities/classes/pagination.class';
import { Routes } from 'src/app/accountancy/utilities/classes/routes.class';
import { WaitHandler } from 'src/app/accountancy/utilities/classes/wait-handler.class';
import { CashAdvanceService } from './cash-advance-service/cash-advance.service';
import { CashAdvanceModifiedInterface } from 'src/app/accountancy/utilities/interfaces/cash-advance.interface';
import { MatDialog } from '@angular/material/dialog';
import { AccSearchTipComponent } from '../../../custom-components/acc-search-tip/acc-search-tip.component';
import { AccTableComponent } from 'src/app/accountancy/custom-components/acc-table/acc-table.component';
import { ManageApproversService } from '../../account-management/manage-approvers/manage-approvers-service/manage-approvers.service';
import { FormGroup } from '@angular/forms';
import { Helper } from 'src/app/accountancy/utilities/classes/helper.class';
import { UserProfileService } from '../../account-management/user-profile/user-profile-service/user-profile.service';
import { LoginService } from 'src/app/accountancy/authentication/login/login-service/login.service';
import { AccSimpleInputTextFormService } from 'src/app/accountancy/custom-components/acc-simple-input-text-form/acc-simple-input-text-form-service/acc-simple-input-text-form.service';
import { GlobalApiService } from 'src/app/accountancy/utilities/services/global-api/global-api.service';

@Component({
  selector: 'app-cash-advance',
  templateUrl: './cash-advance.component.html',
  styleUrls: ['./cash-advance.component.scss']
})
export class CashAdvanceComponent implements OnDestroy {

  public readonly routes = Routes;

  public readonly waitHandler = new WaitHandler();
  public readonly pagination = new Pagination();
  public readonly caList$ = new BehaviorSubject<CashAdvanceModifiedInterface[]>([]);

  public readonly fgCaFilter = this._newFgCaFilter();

  private readonly _subsGetCaList = this._getSubsGetCaList();

  public readonly caApprovalStatuses$ = this._getCaApprovalStatuses();

  constructor(
    private _caS: CashAdvanceService,
    private _mApprovalS: ManageApproversService,
    private _loginS: LoginService,
    private _gApiS: GlobalApiService,
    private _simpleFormS: AccSimpleInputTextFormService,
    private _matDialog: MatDialog,
  ) { }

  public readonly dialogs = {
    openSearch: () => this._matDialog.open(AccSearchTipComponent),
    openPrint: async (table: AccTableComponent) => table.printTable(),
  }

  public ngOnDestroy(): void {
    this._subsGetCaList.unsubscribe();
  }

  private _newFgCaFilter() {
    return new FormGroup({
      Type: Helper.reactiveForms.control<string>('My Requests'),
      Status: Helper.reactiveForms.control<string>('All Status'),
    });
  }

  private _getSubsGetCaList() {
    return combineLatest([
      this.fgCaFilter.valueChanges.pipe(startWith(this.fgCaFilter.value)),
      this.pagination.searchQuery$.pipe(startWith(this.pagination.query), debounceTime(500), distinctUntilChanged()),
      this.pagination.pageLimit$,
      this.pagination.currentPage$,
      this.pagination.reloader$.pipe(startWith(null)),
    ]).pipe(
      tap(() => {
        this.waitHandler.newRequest();
        this.caList$.next([])
      }),
      switchMap(([filter, query, limit]) => {
        const userId = this._loginS.userData?.ID;
        const requestFilter = {
          RequestedBy: filter?.Type && filter.Type === 'My Requests' ? userId : null,
          Approver: filter?.Type && filter.Type === 'For My Approval' ? userId : null,
          Status: filter?.Status && filter.Status === 'All Status' ? null : filter?.Status,
        };

        return this._caS.getAllCashAdvanceRequest(this.pagination.start, limit, query, requestFilter).pipe(
          finalize(() => this.waitHandler.finishRequest()),
          map(values => values.body),
          tap(values => {
            this.pagination.setTotalCount(values?.total_rows);
            this.caList$.next(values?.data.map(v => ({ ...v, checked: false } as CashAdvanceModifiedInterface)) ?? []);
          }),
        );
      }),
    ).subscribe();
  }

  private _getCaApprovalStatuses() {
    return of(null).pipe(
      tap(() => this.waitHandler.newRequest()),
      switchMap(() => this._mApprovalS.getApprovalLevels("Cash Advance").pipe(
        finalize(() => this.waitHandler.finishRequest()),
      ))
    )
  }

  public newCa() {
    const isRequesting$ = new BehaviorSubject(false);
    const departmentList$ = new BehaviorSubject<{ value: any, key: string }[]>([]);
    const detachmentList$ = new BehaviorSubject<{ value: any, key: string }[]>([]);

    const subsDepartment: Subscription = of(null).pipe(
      tap(() => isRequesting$.next(true)),
      switchMap(() => this._gApiS.getDepartmentList().pipe(
        finalize(() => {
          isRequesting$.next(false);
          subsDepartment.unsubscribe();
        }),
        tap(data => departmentList$.next(data.map(v => ({ value: `(${v.ID}) ${v.Name}`, key: `(${v.ID}) ${v.Name}` }))))
      ))
    ).subscribe();

    const subsDetachment: Subscription = of(null).pipe(
      tap(() => isRequesting$.next(true)),
      switchMap(() => this._gApiS.getDetachmentList().pipe(
        finalize(() => {
          isRequesting$.next(false);
          subsDetachment.unsubscribe();
        }),
        tap(data => {
          const list = data?.map(v => ({ value: `(${v.ID}) ${v.Detachment}`, key: `(${v.ID}) ${v.Detachment}` })) ?? [];
          detachmentList$.next([{ value: '', key: 'No Detachment' }, ...list]);
        })
      ))
    ).subscribe();

    return this._simpleFormS.open({
      title: 'Request Cash Advance',
      formClass: "min-w-[400px]",
      isRequesting$,
      controls: [
        { name: "Purpose", inputType: "text", required: true, colClassName: "col-span-12 mb-4" },
        { name: "Description", inputType: "text", required: true, colClassName: "col-span-12 mb-4" },
        { name: "Amount", label: "PHP", inputType: "number", required: true, colClassName: "col-span-12 mb-4" },
        { name: "Department", inputType: "select", required: true, colClassName: "col-span-12 md:col-span-6 mb-4", selectOptions$: departmentList$ },
        { name: "Detachment", inputType: "select", colClassName: "col-span-12 md:col-span-6 mb-4", selectOptions$: detachmentList$, initialValue: '' },
        { name: "Remarks", inputType: "text", colClassName: "col-span-12 mb-4" },
      ],
      onSubmit: (formData, ref) => {
        const formBody = {
          DateOfUse: Helper.date.format(new Date(), 1, '-'),
          TransactionDate: Helper.date.format(new Date(), 1, '-'),
          Purpose: formData["Purpose"],
          Amount: Number(formData["Amount"]),
          DollarAmount: 1,
          Description: formData["Description"],
          ChargeTo: `${formData["Department"]}${formData["Detachment"] ? ' - ' + formData["Detachment"] : ''}`,
          Remarks: formData["Remarks"],
          RequestedBy: this._loginS.userData?.ID,
        };

        const subs: Subscription = of(null).pipe(
          tap(() => {
            this.waitHandler.newRequest();
            isRequesting$.next(true);
          }),
          switchMap(() => this._caS.newCashAdvanceRequest(formBody).pipe(
            finalize(() => {
              this.waitHandler.finishRequest();
              isRequesting$.next(false);
              this.pagination.reloader$.next();
              ref?.close();
              subs.unsubscribe();
            }),
          ))
        ).subscribe();
      }
    }, true);
  }
}
